import React, { useEffect, useMemo, useState } from 'react';
import { Alert, alpha, Box, Button, CircularProgress, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiGet } from 'api/service';
import { GREEN, MONO, SEV_COLOR } from './scanUi';
import { FILTERS, STATE, scansLine, visible } from './repeatLogic';

const panel = { borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' };
const Pill = ({ color, children }) => <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, px: 1.3, py: 0.5, borderRadius: 99, bgcolor: alpha(color, 0.16), color, whiteSpace: 'nowrap' }}>{children}</Typography>;

export default function FindingFingerprintsPage() {
  const nav = useNavigate();
  const [data, setData] = useState({ results: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');

  useEffect(() => {
    apiGet('/api/finding-fingerprints/?limit=500').then(setData).catch(() => setError('Could not load your repeated findings.')).finally(() => setLoading(false));
  }, []);

  const rows = data.results;
  const shown = useMemo(() => visible(rows, filter, q), [rows, filter, q]);
  const { reports = 0, problems = 0, came_back: cameBack = 0 } = data.summary;
  const stat = (label, value, sub, color) => (
    <Box sx={{ ...panel, p: 2.2, flex: '1 1 200px' }}>
      <Typography variant="overline" color="text.secondary">{label}</Typography>
      <Typography sx={{ fontSize: 28, fontWeight: 700, fontFamily: MONO, color }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{sub}</Typography>
    </Box>
  );

  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress sx={{ color: GREEN }} /></Box>;
  return (
    <Stack spacing={2.5} className="fuzzscan-page" sx={{ p: 3 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Repeated findings</Typography>
        <Typography color="text.secondary" variant="body2">Problems that keep showing up, worst and most repeated first</Typography>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}

      <Stack direction="row" gap={2} flexWrap="wrap">
        <Box sx={{ ...panel, p: 3, flex: '2 1 440px', background: `linear-gradient(135deg, ${alpha(GREEN, 0.12)}, transparent 65%)` }}>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>Noise removed</Typography>
          <Stack direction="row" alignItems="baseline" gap={2} flexWrap="wrap">
            <Typography sx={{ fontSize: 44, fontWeight: 800, fontFamily: MONO, color: 'text.secondary', textDecoration: reports > problems ? 'line-through' : 'none' }}>{reports}</Typography>
            <Typography sx={{ fontSize: 28, color: 'text.secondary' }}>→</Typography>
            <Typography sx={{ fontSize: 44, fontWeight: 800, fontFamily: MONO, color: GREEN }}>{problems}</Typography>
          </Stack>
          <Typography color="text.secondary">
            {reports > problems ? `${reports} reports across your scans are really ${problems} distinct problems. You only fix each one once.` : 'Every report so far is a different problem.'}
          </Typography>
        </Box>
        {stat('Came back after fixed', cameBack, cameBack ? 'the fix did not hold' : 'nothing came back', cameBack ? '#ef4444' : GREEN)}
      </Stack>

      <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
        {FILTERS.map((f) => {
          const on = filter === f.id;
          return (
            <Button key={f.id} onClick={() => setFilter(f.id)} sx={{ borderRadius: 99, px: 2, textTransform: 'none', fontWeight: 600, border: 1, borderColor: on ? 'text.secondary' : 'divider', color: on ? 'text.primary' : 'text.secondary', bgcolor: on ? alpha('#fff', 0.06) : 'transparent' }}>
              {f.label} {rows.filter(f.test).length}
            </Button>
          );
        })}
        <TextField size="small" placeholder="Search by problem or website" value={q} onChange={(e) => setQ(e.target.value)} sx={{ ml: 'auto', minWidth: 260 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
      </Stack>

      <Box sx={{ ...panel, overflow: 'hidden' }}>
        <Typography sx={{ p: 2.2, fontWeight: 700 }}>Repeat offenders</Typography>
        {!shown.length && <Typography color="text.secondary" sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>{rows.length ? 'Nothing matches that.' : 'Nothing yet. Findings that appear in more than one scan will show up here.'}</Typography>}
        {shown.map((r) => (
          <Stack key={r.fingerprint} direction="row" alignItems="center" gap={2.5} flexWrap="wrap" sx={{ px: 2.8, py: 1.8, borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ flex: '2 1 300px', minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600 }}>{r.title}</Typography>
              <Typography variant="caption" color="text.secondary"><span style={{ fontFamily: MONO }}>{r.website}</span> · {scansLine(r)}</Typography>
            </Box>
            <Box sx={{ flex: '1 1 140px' }}>
              <Stack direction="row" gap={0.5}>
                {r.timeline.map((hit, i) => <Box key={i} sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: hit ? GREEN : 'divider' }} />)}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>last {r.timeline.length} scans, oldest first</Typography>
            </Box>
            <Pill color={SEV_COLOR[r.severity] || '#64748b'}>{r.severity.toUpperCase()}</Pill>
            <Pill color={STATE[r.state].color}>{STATE[r.state].label}</Pill>
            <Button size="small" variant="outlined" color="inherit" onClick={() => nav('/vulns')}>Open Findings</Button>
          </Stack>
        ))}
      </Box>
      <Typography variant="body2" color="text.secondary">Each problem gets a short ID so the same one is never counted twice. Repeats of one problem are merged into a single entry here.</Typography>
    </Stack>
  );
}
